import { getCurrentAppConfigApiHost } from "../config/appConfig"

import { request } from "./request"

export interface RecognizedLabel {
  id: string
  name: string
  count: number
}

export interface GetLabelsResponse {
  labels: RecognizedLabel[]
  total: number
}

export interface GetLabelsParams {
  limit?: number
  offset?: number
  search?: string
}

export interface GetTagsParams {
  limit?: number
  offset?: number
  search?: string
}

export interface GetAssetsParams {
  limit?: number
  offset?: number
  search?: string
}

export interface SearchNode {
  id: string
  name: string
  type: 'file' | 'dir'
  parent_id: string
  is_trashed: boolean
  is_explicitly_trashed: boolean
  is_shared: boolean
  created: string
  updated: string
  file_info?: {
    id: string
    size: number
    checksum: string
    type: string
    mime: string
  }
  _links: {
    self: { href: string }
    set_share: { href: string }
    children: { href: string }
    content: { href: string }
  }
}

export interface SearchResponse {
  id: string
  created: string
  updated: string
  query: {
    path?: string
    name?: string
    field_values?: Array<{
      id: string
      filters: {
        and_values?: string[]
        or_values?: string[]
      }
    }>
  }
  expires: string
  _links: {
    self: { href: string }
  }
  _embedded: {
    nodes: SearchNode[]
  }
}

export interface SearchParams {
  path?: string
  name_pattern?: string
  labels_pattern?: string
  labels_or_name_pattern?: string
  limit?: number
  type?: 'file' | 'dir'
  field_values?: Array<{
    id: string
    filters: {
      and_values?: string[]
      or_values?: string[]
      not_values?: string[]
    }
  }>
}

export interface BatchRequest {
  method: 'POST'
  url: string
  params: SearchParams
}

export interface BatchResponse {
  id: string
  state: 'created' | 'in_process' | 'done'
  created: string
  updated: string
  requests: BatchRequest[]
  responses?: Array<{
    status_code: number
    body: SearchResponse
  }>
}

export interface BatchOpResponse {
  status: number;
  headers: string[][];
  json_body: Record<string, any>
}

export interface BatchOpRequest {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS' | 'HEAD';
  url: string;
  headers: string[][];
  params: Record<string, any>;
}

export interface BatchedOpSchema {
  _links: {
    self: {
      href: string;
      templated?: boolean;
    };
  };
  created: string;
  updated: string;
  id: string;
  state: 'created' | 'in_process' | 'done';
  author_id: number;
  author_token_id: string;
  user_agent: string;
  requests: Array<BatchOpRequest>;
  responses: Array<BatchOpResponse>;
}

export interface BatchRetryError extends Error {
  failedOperations: BatchedOpSchema[];
  totalAttempts: number;
}

export const searchApi = {
  getLabels: (userId: number, params?: GetLabelsParams) => {
    return request('GET', `/api/2/users/${userId}/fs/recognized_labels`, { limit: params?.limit, offset: params?.offset, search: params?.search })
  },
  getLabelsByValue: (userId: number, value: string) => {
    return request('GET', `/api/2/users/${userId}/fs/recognized_labels`, { name_pattern: value + '*' })
  },
  search: (userId: number, params: SearchParams): Promise<SearchResponse> => {
    return request('POST', `/api/2/users/${userId}/fs/root/searcher`, params)
  },
  getBatchOperation: (userId: number, operationId: string): Promise<BatchedOpSchema> => {
    return request('GET', `/api/2/users/${userId}/batched_ops/${operationId}`)
  },
  waitForBatchCompletion: async (userId: number, operationId: string, maxAttempts = 30, delayMs = 1000): Promise<BatchedOpSchema> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const operation = await searchApi.getBatchOperation(userId, operationId)

      if (operation.state === 'done') {
        return operation
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Batch operation timeout')
  },
  /**
   * Executes batch search operations with automatic retry mechanism for failed requests
   *
   * Algorithm:
   * for each chunk:
   *   initialize successful requests/responses accumulators
   *   for attempt = 0 to maxRetries:
   *     execute batch operation with current chunk
   *     wait for completion
   *
   *     extract successful responses and accumulate them
   *     get failed request indexes
   *
   *     if no failed requests:
   *       break // chunk completed successfully
   *
   *     if attempt < maxRetries:
   *       create new chunk with failed requests only
   *       wait with exponential backoff
   *       continue retry loop
   *     else:
   *       save failed operations for error reporting
   *
   *   if accumulated successful requests exist:
   *     create combined operation with all successful results
   *
   * @param userId - User ID for the search operation
   * @param searchParams - Array of search parameters to execute
   * @param batchSize - Maximum number of requests per batch (default: 100)
   * @param maxRetries - Maximum number of retry attempts (default: 10)
   * @param retryTimeout - Base timeout for exponential backoff in ms (default: 1000)
   * @returns Combined results from all successful batch operations
   * @throws BatchRetryError if any operations fail after all retries
   */
  batchSearch: async (userId: number, searchParams: SearchParams[], batchSize = 100, maxRetries = 10, retryTimeout = 300): Promise<BatchedOpSchema> => {
    const apiHost = getCurrentAppConfigApiHost()

    // Helper function to get failed request indexes
    const getFailedRequestIndexes = (operation: BatchedOpSchema): number[] => {
      return operation.responses
        .map((response, index) => response.status >= 400 ? index : -1)
        .filter(index => index !== -1)
    }

    // Helper function for exponential backoff delay
    const getRetryDelay = (attempt: number, baseTimeout: number): number => {
      const delay = baseTimeout * Math.pow(2, attempt)
      return Math.min(delay, 30000) // Cap at 30 seconds
    }

    // Split into chunks of specified size
    const chunks: SearchParams[][] = []
    for (let i = 0; i < searchParams.length; i += batchSize) {
      chunks.push(searchParams.slice(i, i + batchSize))
    }

    // Execute batch operations for each chunk with retry logic
    const operations: BatchedOpSchema[] = []
    const failedOperations: BatchedOpSchema[] = []

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      let currentChunk = chunks[chunkIndex]
      let retryAttempt = 0
      let chunkCompleted = false

      if (currentChunk.length === 1) {
        const singleParams = currentChunk[0]
        try {
          const singleJson = await request('POST', `/api/2/users/${userId}/fs/root/searcher`, singleParams)
          operations.push({
            _links: { self: { href: '', templated: false } },
            created: '',
            updated: '',
            id: '',
            state: 'done',
            author_id: userId,
            author_token_id: '',
            user_agent: '',
            requests: [{
              method: 'POST',
              url: `${apiHost}/api/2/users/${userId}/fs/root/searcher`,
              headers: [],
              params: singleParams,
            }],
            responses: [{
              status: 200,
              headers: [],
              json_body: singleJson,
            }],
          })
          continue
        } catch (error) {
          throw error
        }
      }

      const chunkSuccessfulRequests: BatchRequest[] = []
      const chunkSuccessfulResponses: Array<BatchOpResponse> = []
      let lastCompletedOperation: BatchedOpSchema | null = null

      while (retryAttempt <= maxRetries && !chunkCompleted) {
        try {
          const requests: BatchRequest[] = currentChunk.map(params => ({
            method: 'POST',
            url: `${apiHost}/api/2/users/${userId}/fs/root/searcher`,
            params
          }))

          const initialResponse: BatchResponse = await request('POST', `/api/2/users/${userId}/batched_ops/`, { requests })

          const completedOperation = await searchApi.waitForBatchCompletion(userId, initialResponse.id)
          lastCompletedOperation = completedOperation

          const failedIndexes = getFailedRequestIndexes(completedOperation)

          completedOperation.responses.forEach((response, index) => {
            if (response.status < 400) {
              chunkSuccessfulRequests.push(completedOperation.requests[index])
              chunkSuccessfulResponses.push(response)
            }
          })

          if (failedIndexes.length) {
            if (retryAttempt < maxRetries) {
              currentChunk = failedIndexes.map(index => currentChunk[index])
              retryAttempt++

              const delay = getRetryDelay(retryAttempt - 1, retryTimeout)
              await new Promise(resolve => setTimeout(resolve, delay))
              continue
            } else {
              failedOperations.push({
                ...completedOperation,
                requests: failedIndexes.map(index => completedOperation.requests[index]),
                responses: failedIndexes.map(index => completedOperation.responses[index])
              })
            }
          }

          chunkCompleted = true
        } catch (error) {
          if (retryAttempt < maxRetries) {
            retryAttempt++
            const delay = getRetryDelay(retryAttempt - 1, retryTimeout)
            await new Promise(resolve => setTimeout(resolve, delay))
          } else {
            throw error
          }
        }
      }

      if (chunkSuccessfulRequests.length && lastCompletedOperation) {
        operations.push({
          ...lastCompletedOperation,
          state: 'done',
          requests: [...chunkSuccessfulRequests],
          responses: [...chunkSuccessfulResponses]
        })
      }
    }

    // If there are failed operations after all retries, throw error
    if (failedOperations.length > 0) {
      const error = new Error(`Batch search failed after ${maxRetries} retries`) as BatchRetryError
      error.failedOperations = failedOperations
      error.totalAttempts = maxRetries + 1
      throw error
    }

    // Combine results from all chunks into single BatchedOpSchema
    if (!operations.length) {
      return {
        id: '',
        state: 'done',
        requests: [],
        responses: []
      }
    }

    return {
      ...operations[0],
      requests: operations.flatMap(op => op.requests),
      responses: operations.flatMap(op => op.responses),
      state: 'done'
    }
  }
}
